/**
 * Fórmulas de Declaración de Renta - Régimen 14 D N°8 (Pro Pyme Transparente), comunes
 * a los AT 2025 y 2026 (recuadros 22 y 23 con los mismos códigos en ambos F22).
 *
 * La base imponible (recuadro 22) no se recalcula: se toma de la DJ 1947 guardada del
 * mismo año con services/dj1947/calculo.js, el mismo cálculo de la DJ. Aquí se arman el
 * recuadro 23 (CPTS) y la liquidación, que en este régimen queda en cero: la empresa no
 * paga primera categoría y sus PPM pasan a los propietarios por la DJ 1947 (columna C14),
 * que los rebajan en su propio F22 (código 1645).
 *
 * Todas las fórmulas son funciones puras que reciben el estado del formulario (data).
 */

const calculo = require('../../../../services/dj1947/calculo');

const round = (val) => Math.round(Number(val) || 0);
const sum = (...vals) => vals.reduce((a, b) => a + round(b), 0);

const baseDJ = (d) => d.dj1947?.base || {};
const repartoDJ = (d) => d.dj1947?.reparto || [];

// ============================================================================
// RECUADRO 22 - Base imponible, desde la DJ 1947
// ============================================================================

const r22 = {
  linea: (d, codigo) => calculo.valorLinea(baseDJ(d), codigo),
  totalIngresos: (d) => calculo.totales(baseDJ(d)).ingresos,          // 1610
  totalEgresos: (d) => calculo.totales(baseDJ(d)).egresos,            // 1629
  baseImponible: (d) => calculo.totales(baseDJ(d)).baseImponible,     // 1630
};

// ============================================================================
// ANTECEDENTES
// ============================================================================

const antecedentes = {
  totalCapitalHistorico: (d) => sum(...(d.antecedentes.socios || []).map((s) => s.capitalHistorico)),
  // Igual que en 14 D N°3: sin CPT del año anterior, la empresa inicia actividades e
  // informa el capital aportado
  capitalAportado1573: (d) => (
    d.antecedentes.cptInicialPositivo || d.antecedentes.cptInicialNegativo ? 0 : antecedentes.totalCapitalHistorico(d)
  ),
};

// ============================================================================
// RECUADRO 23 - CPTS
// ============================================================================

const r23 = {
  cptInicialPositivo: (d) => round(d.antecedentes.cptInicialPositivo),          // 1580 (+)
  cptInicialNegativo: (d) => round(d.antecedentes.cptInicialNegativo),          // 1582 (-)
  capitalAportado: (d) => antecedentes.capitalAportado1573(d),                  // 1573 (+)
  aumentosCapital: (d) => round(d.antecedentes.aumentosCapital),                // 1574 (+)
  disminucionesCapital: (d) => round(d.antecedentes.disminucionesCapital),      // 1575 (-)
  // Una pérdida va en el 1713, no en negativo en el 1712
  baseImponible: (d) => Math.max(r22.baseImponible(d), 0),                      // 1712 (+)
  perdida: (d) => Math.max(-r22.baseImponible(d), 0),                           // 1713 (-)
  // Se rebajaron en el recuadro 22 (1627) y vuelven a sumar en el CPTS
  perdidasAnteriores: (d) => r22.linea(d, '1627'),                              // 1714 (+)
  retiros: (d) => sum(...repartoDJ(d).map((fila) => fila.retiros)),             // 1576 (-)
  ingresoDiferido: (d) => r22.linea(d, '1608'),                                 // 1715 (-)
  gastosNoAceptados: (d) => sum(d.antecedentes.pagoInteresesMultas, d.antecedentes.pagoIDPCAnterior), // 1577 (-)
  credito33bis: (d) => r22.linea(d, '1609'),                                    // 1716 (-)
  creditoIDPC: (d) => r22.linea(d, '1605'),                                     // 1578 (-)
  otrasAgregar: (d) => round(d.antecedentes.otrasPartidasAgregar),              // 1584 (+)
  otrasDeducir: (d) => round(d.antecedentes.otrasPartidasDeducir),              // 1585 (-)
  resultado: (d) => (
    r23.cptInicialPositivo(d) - r23.cptInicialNegativo(d) + r23.capitalAportado(d)
    + r23.aumentosCapital(d) - r23.disminucionesCapital(d)
    + r23.baseImponible(d) - r23.perdida(d) + r23.perdidasAnteriores(d)
    - r23.retiros(d) - r23.ingresoDiferido(d) - r23.gastosNoAceptados(d)
    - r23.credito33bis(d) - r23.creditoIDPC(d)
    + r23.otrasAgregar(d) - r23.otrasDeducir(d)
  ),
  cptsPositivoFinal: (d) => Math.max(r23.resultado(d), 0),                      // 1581
  cptsNegativoFinal: (d) => Math.max(-r23.resultado(d), 0),                     // 1583
};

// ============================================================================
// LIQUIDACIÓN
// ============================================================================

const liquidacion = {
  resultado305: () => 0,
  // Reajuste de los PPM al 31.12, tal como quedó en la DJ 1947 (va en el 1607)
  reajustePPM: (d) => round(baseDJ(d).ppm_reajuste),
  ppmPuestosADisposicion: (d) => sum(...repartoDJ(d).map((fila) => fila.ppm)),
};

/**
 * Lo que impide calcular: sin DJ 1947 del año, o con errores (porcentajes que no suman
 * 100 %, …). Devuelve el mensaje, o null si se puede calcular.
 */
const validar = (data) => {
  if (!data.dj1947 || !data.dj1947.base) {
    return `No hay DJ 1947 del AT ${data.anioTributario}. Prepárala primero en Declaraciones juradas: la renta 14 D N°8 usa su base imponible y su reparto.`;
  }
  const { errores } = calculo.revisar(data.dj1947.base, data.dj1947.propietarios || []);
  if (errores.length) {
    return `La DJ 1947 del AT ${data.anioTributario} tiene errores: ${errores.join(' ')} Corrígela en Declaraciones juradas.`;
  }
  return null;
};

// ============================================================================
// FUNCIÓN PRINCIPAL: recalcula todos los campos computados
// ============================================================================

const recalculate = (data) => ({
  antecedentes: {
    totalCapitalHistorico: antecedentes.totalCapitalHistorico(data),
    capitalAportado1573: antecedentes.capitalAportado1573(data),
  },
  r22: {
    totalIngresos: r22.totalIngresos(data),
    totalEgresos: r22.totalEgresos(data),
    baseImponible: r22.baseImponible(data),
  },
  r23: Object.fromEntries(Object.keys(r23).map((campo) => [campo, r23[campo](data)])),
  liquidacion: {
    resultado305: liquidacion.resultado305(data),
    reajustePPM: liquidacion.reajustePPM(data),
    ppmPuestosADisposicion: liquidacion.ppmPuestosADisposicion(data),
  },
  propietarios: repartoDJ(data),
  totalesPropietarios: calculo.totalesDeclaracion(repartoDJ(data)),
});

module.exports = {
  antecedentes,
  r22,
  r23,
  liquidacion,
  validar,
  recalculate,
};
